<template>
    <div class="card">
        <div class="card-body">
            <div role="group" class="form-row form-group mb-2 justify-content-between">
                <h4 class="col-xs card-title"><em class="fas fa-users fa-fw mr-2 text-muted"></em> {{ 'admin.userlist.header' | lang }}</h4>
                <div class="col-xs filter">
                    <b-input-group size="sm">
                        <b-form-input
                                id="filter-input"
                                v-model="filter"
                                type="search"
                                :placeholder="Lang.get('admin.userlist.searchText')"
                                debounce="500"
                        ></b-form-input>
                        <b-input-group-append>
                            <b-button :disabled="!filter" @click="filter = ''">{{ 'admin.userlist.clear' | lang }}</b-button>
                        </b-input-group-append>
                    </b-input-group>
                </div>
            </div>
            <b-table
                    show-empty
                    :responsive="true"
                    :items="userProvider"
                    :busy.sync="isBusy"
                    :fields="fields"
                    :current-page="currentPage"
                    :per-page="perPage"
                    ref="table"
                    :filter="filter"
            >
                <template v-slot:cell(user)="data">
                    <a v-if="data.item.edit" :href="noScheme(data.item.edit)">
                        <i class="mdi mdi-account-edit"></i> {{ data.item.name }}
                    </a>
                    <span v-else class="text-white">
                        {{ data.item.name }}
                    </span>
                    <div>
                        {{ data.item.email }}
                    </div>
                </template>
                <template v-slot:cell(roles)="data">
                    <template v-if="data.item.roles && data.item.roles.length">
                        <span v-for="role in data.item.roles" class="user-role-badge" :class="roleClass(role.name)">{{ role.name }}</span>
                    </template>
                    <template v-else>
                        <i class="text-muted">{{ 'general.none' | lang }}</i>
                    </template>
                </template>
                <template v-slot:cell(channel)="data">
                    <div v-if="data.item.channel" class="d-inline-block">
                        <div class="d-inline-block">
                            <a :href="data.item.channel.link"
                               target="_blank" title="@lang('youtube.info.channel')">{{ data.item.channel.name }}</a>
                            <span v-if="data.item.deactivated" class="deactivated">({{ 'manager.action.deactivate.deactivatedTitle' | lang }})</span>
                            <div class="small d-none d-xl-block" :title="Lang.get('youtube.info.channelId')">{{ data.item.channel.youtube_id }}</div>
                        </div>
                    </div>
                </template>
                <template v-slot:cell(subscriptions)="data">
                    <div class="module-subscriptions">
                        <template v-for="subscription in data.item.subscriptions">
                            <div v-if="subscription.active" class="module-subscription" data-toggle="tooltip" data-placement="top" :title="subscription.name">
                                <i :class="subscription.icon"></i>
                            </div>
                        </template>
                    </div>
                </template>
                <template v-slot:cell(revenue)="data">
                    <div class="text-right" v-if="!isNaN(data.item.payable)">
                        <span :class="{ 'text-success': data.item.payable > 0 }">
                            {{ data.item.payable | formatCurrency('USD', 'en-US') }}
                        </span>
                    </div>
                </template>
                <template v-slot:cell(impersonate)="data">
                    <a v-if="data.item.impersonate" class="btn btn-secondary btn-static"
                       :href="data.item.impersonate">{{ 'admin.impersonate.start' | lang }}</a>
                </template>
            </b-table>
            <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="user-table"
            ></b-pagination>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'UserList',
  props: {
    url: String,
  },
  data() {
    return {
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      users: [],
      isBusy: false,
      filter: '',
      defaultFields: [
        {key: 'user', label: Lang.get('admin.userlist.name')},
        {key: 'roles', label: Lang.get('admin.userlist.roles')},
        {key: 'channel', label: Lang.get('admin.userlist.channel')},
        {key: 'subscriptions', label: Lang.get('admin.userlist.subscriptions')},
        {key: 'revenue', label: Lang.get('admin.userlist.payableRevenue')},
        {key: 'impersonate', label: Lang.get('admin.userlist.impersonate')},
      ],
    };
  },
  computed: {
    fields() {
      return this.defaultFields;
    },
  },
  watch: {
    filter(newFilter, oldFilter) {
      if (newFilter !== oldFilter) {
        // Current page is invalid when filter is applied
        this.currentPage = 1
      }
    }
  },
  methods: {
    roleClass(value) {
      return `user-role-badge-${value}`;
    },
    noScheme(url) {
      return url.replace(/^https?:/, '');
    },
    userProvider(ctx) {
      let url = this.noScheme(this.url);
      url += `?page=${ctx.currentPage}&size=${ctx.perPage}&filter=${ctx.filter}`;
      const promise = axios.get(url);

      return promise.then(response => {
        const data = response.data.data;
        const meta = response.data.meta;
        this.currentPage = meta.current_page;
        this.totalRows = meta.total;
        this.users = data || [];

        return this.users;
      }).catch(error => {
        // console.log(error);

        return [];
      });
    },
    refresh() {
      this.$nextTick(() => {
        this.$refs.table.refresh();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.filter {
  min-width: 420px;
}
input#filter-input {
  height:35px;
  padding-left: 10px;
  border: solid rgba(68, 68, 94, 0.36);
  border-width: 0 0 0.5px 0.5px;
  border-radius: 0;
  &:focus {
    border-bottom: 2px solid #ff990d;
  }
}
.input-group-append {
  margin-left: 0;
}
div.input-group-append button.btn.btn-secondary {
  border-radius: 0;
}
</style>
